import { z } from 'zod';
/**
 * Who a user is to the platform.
 *
 * - `founder`      — an end customer. Sees only their own projects.
 * - `developer`    — Aldemis staff. Reviews specs and stories and supervises
 *   builds, but only for the projects a super admin has assigned to them.
 * - `super_admin`  — Aldemis staff who can do everything: work on any project,
 *   invite developers and end users, and assign projects to developers.
 */
export const ROLES = ['founder', 'developer', 'super_admin'];
export const roleSchema = z.enum(ROLES);
/**
 * Staff roles may enter the admin area.
 *
 * Deliberately not modelled as a numeric rank: `developer` is not "above"
 * `founder`, it is *beside* it. Staff access is broad but never implies
 * ownership, and founder access is narrow but exclusive to their own data.
 * Every founder-scoped query must still filter on ownership — see
 * {@link canAccessOwnedResource}.
 */
export function isStaff(role) {
    return role === 'developer' || role === 'super_admin';
}
/** A super admin: full run of the platform, including inviting users. */
export function isSuperAdmin(role) {
    return role === 'super_admin';
}
/**
 * The pure part of project authorisation: a super admin may reach anything, and
 * a founder may reach only rows they own — `ownerUserId` must come from the
 * stored row, never from the request, or this check is worthless.
 *
 * A `developer` is intentionally NOT granted access here: their reach is limited
 * to the projects a super admin has assigned to them, which needs a database
 * lookup. That is layered on top of this predicate at the query layer (see
 * `actorCanAccessProject` in the API), so this function stays synchronous and
 * dependency-free.
 */
export function canAccessOwnedResource(actor, resource) {
    if (isSuperAdmin(actor.role))
        return true;
    return actor.id === resource.ownerUserId;
}
//# sourceMappingURL=roles.js.map